import React from 'react';
import {
    AlertsIcon,
    ApiIcon,
    BuildersIcon,
    CohortIcon,
    FavoritesIcon,
    FlowsIcon,
    HeatmapIcon,
    HipIcon,
    HomeIcon,
    LeaderboardIcon,
    LiquidationsIcon,
    ListItemButtonContent,
    MarketRadarIcon,
    PerpsIcon,
    StatsIcon,
    StyledItemText,
    StyledLink,
    StyledListItem,
    DashboardIcon,
    KeyIcon,
    LinkIcon,
} from '@/components/Hypertracker/HypertrackerSidebar/HypertrackerSidebarStyled';
import { useNavigate } from 'react-router';
import { Box, Stack } from '@mui/system';
import { Badge, Divider, ListItemButton, ListItemIcon, Typography, useTheme } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useDispatch } from 'react-redux';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { setModalWindow } from '@/state/hypertracker/hypertackerSlice';
import { MODAL_TYPE_HANDLER } from '@/lib/types/hypertrackerTypes';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { ROUTES } from '@/lib/constants';

interface SidebarItemProps {
    menuItem: { id?: string; label: string; url: string };
    open: boolean;
    isSelected: boolean;
    t: (key: string) => string;
    totalActiveAlertsFormatted: string;
}

const SidebarItem = ({
    menuItem,
    open,
    isSelected,
    t,
    totalActiveAlertsFormatted,
}: SidebarItemProps) => {
    const dispatch = useDispatch();
    const isMemberAuthorized = useIsMemberAuthorized();
    const theme = useTheme();
    const navigate = useNavigate();
    const itemId = menuItem.id ?? menuItem.label;

    const onNotAuthorizedClick = () => {
        dispatch(
            setModalWindow({
                modalType: MODAL_TYPE_HANDLER.IS_AUTH_MODAL,
                authorizationModalType: AUTH_TYPE.SIGN_IN,
            }),
        );
    };

    const onMenuItemClick = (menuItem: { label: string; url: string }) => {
        if (!isMemberAuthorized && menuItem.label === 'menu_favorites') {
            onNotAuthorizedClick();
            return;
        }

        if (menuItem.url === ROUTES.HOME) {
            window.open(menuItem.url, '_blank');
            return;
        }

        const route = menuItem.url;
        if (route.includes(':id')) return;
        if (!route) return;
        navigate(route);
    };

    const getIcon = (iconName: string) => {
        switch (iconName) {
            case 'menu_home':
                return <HomeIcon />;
            case 'menu_heatmap':
                return <HeatmapIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_cohorts':
                return <CohortIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_leaderboard':
                return <LeaderboardIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_perps':
                return <PerpsIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_stats':
                return <StatsIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_builders':
                return <BuildersIcon />;
            case 'menu_alerts':
                return <AlertsIcon />;
            case 'menu_api':
                return menuItem.label === 'menu_api_new' ? (
                    <KeyIcon sx={{ path: { stroke: 'currentColor' } }} />
                ) : (
                    <ApiIcon />
                );
            case 'menu_favorites':
                return <FavoritesIcon />;
            case 'menu_flows':
                return <FlowsIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_liquidations':
                return <LiquidationsIcon sx={{ path: { stroke: 'currentColor' } }} />;
            case 'menu_radar':
                return <MarketRadarIcon sx={{ path: { fill: 'currentColor' } }} />;
            case 'menu_hip3':
                return <HipIcon sx={{ path: { fill: 'currentColor' } }} />;
            case 'menu_cmm_dashboard':
                return (
                    <DashboardIcon
                        sx={{
                            path: { fill: theme.palette.primary.main },
                        }}
                    />
                );
            default:
                return null;
        }
    };

    const isArrowAvailable = (menuItem: string) => {
        return menuItem === 'menu_cohorts';
    };

    const isExternal = (menuItem: { label: string }) => {
        return menuItem.label === 'menu_cmm_dashboard';
    };

    return (
        <>
            <StyledListItem
                key={itemId}
                disablePadding
                disableGutters
                sx={{
                    marginTop: itemId === 'menu_api' ? '12px' : '0',
                }}
            >
                <StyledLink
                    data-testid={`hypertracker-sidebar-menu-item-${itemId}`}
                    href={menuItem.url}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        if (
                            e.button === 0 &&
                            !e.ctrlKey &&
                            !e.metaKey &&
                            !e.shiftKey &&
                            !e.altKey
                        ) {
                            e.preventDefault();
                            onMenuItemClick(menuItem);
                        }
                    }}
                >
                    {itemId === 'menu_api' && (
                        <Box sx={{ padding: '0 8px', margin: '8px 0' }}>
                            <Divider orientation="horizontal" />
                        </Box>
                    )}
                    <ListItemButton
                        disableGutters
                        sx={{
                            minHeight: 32,
                            justifyContent: open ? 'initial' : 'center',
                            padding: '0 12px',
                            '&:hover': { backgroundColor: 'transparent' },
                        }}
                    >
                        <ListItemButtonContent
                            sx={{
                                opacity: 1,
                                color: isSelected
                                    ? theme.palette.common.white
                                    : theme.palette.primitives.woodsmoke[500],
                                '&:hover': { color: theme.palette.common.white },
                                '& .MuiListItemIcon-root': { color: 'inherit' },
                                ...(open && itemId === 'menu_api'
                                    ? {
                                          backgroundColor: theme.palette.primitives.woodsmoke[900],
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                      }
                                    : {}),
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                }}
                            >
                                <Badge
                                    badgeContent={totalActiveAlertsFormatted}
                                    invisible={
                                        !isMemberAuthorized ||
                                        open ||
                                        +totalActiveAlertsFormatted === 0 ||
                                        menuItem.label !== 'menu_alerts'
                                    }
                                    color={'info'}
                                    variant={'standard'}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            left: 5,
                                            top: -4,
                                            fontSize: '10px',
                                        },
                                    }}
                                >
                                    {getIcon(itemId)}
                                </Badge>
                            </ListItemIcon>
                            <StyledItemText
                                sx={{
                                    opacity: open ? 1 : 0,
                                    ...(open && itemId === 'menu_api' ? { flexGrow: 0 } : {}),
                                }}
                                primary={
                                    <Stack
                                        direction={'row'}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Typography
                                            variant={'p14'}
                                            color="inherit"
                                            sx={{
                                                marginLeft: '12px',
                                                textTransform: 'capitalize',
                                            }}
                                            fontWeight={itemId === 'menu_api' ? 500 : 600}
                                        >
                                            {t(menuItem.label)}
                                        </Typography>
                                        {isArrowAvailable(menuItem.label) && (
                                            <ArrowForwardIosIcon
                                                sx={{
                                                    width: '12px',
                                                }}
                                            />
                                        )}
                                        {menuItem.label === 'menu_alerts' &&
                                            isMemberAuthorized &&
                                            +totalActiveAlertsFormatted > 0 && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '2px 8px',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        borderRadius: '8px',
                                                    }}
                                                >
                                                    <Typography
                                                        variant={'p10'}
                                                        color={'customText.text1'}
                                                    >
                                                        {totalActiveAlertsFormatted}
                                                    </Typography>
                                                </Box>
                                            )}
                                        {isExternal(menuItem) && (
                                            <LinkIcon sx={{ path: { stroke: 'currentColor' } }} />
                                        )}
                                    </Stack>
                                }
                            />
                        </ListItemButtonContent>
                    </ListItemButton>
                </StyledLink>
            </StyledListItem>
            {menuItem.label === 'menu_home' && (
                <Box sx={{ padding: '0 8px', margin: '8px 0' }}>
                    <Divider orientation="horizontal" />
                </Box>
            )}
        </>
    );
};

export default SidebarItem;
